"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import AppLogo from "@/components/ui/AppLogo";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import { useTheme } from "@/contexts/ThemeContext";

const publicNavLinks = [
  { href: "/", label: "Home" },
  { href: "/for-pet-owners", label: "For Pet Owners" },
  { href: "/for-businesses", label: "For Businesses" },
  { href: "/how-it-works", label: "How It Works" },
  { href: "/contact", label: "Contact" },
];

const ownerNavLinks = [
  { href: '/dashboard', label: 'Dashboard' },
  { href: '/pets', label: 'My Pets' },
  { href: '/find-professionals', label: 'Find Pros' },
  { href: '/connections', label: 'Connections' },
  { href: '/appointments', label: 'Appointments' },
  { href: '/messages', label: 'Messages' },
];

const professionalNavLinks = [
  { href: "/dashboard", label: "Dashboard" },
  { href: "/business", label: "Business Profile" },
  { href: "/availability", label: "Availability" },
  { href: "/boarding-settings", label: "Boarding" },
  { href: "/bookings", label: "Bookings" },
  { href: "/messages", label: "Messages" },
];

const adminNavLinks = [
  { href: "/admin", label: "Admin Panel" },
  { href: "/admin/users", label: "Users" },
  { href: "/admin/verify-professionals", label: "Verify Pros" },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { userProfile, signOut, isEmailVerified, user } = useAuth();
  const { theme, setTheme } = useTheme();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick as EventListener);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick as EventListener);
    };
  }, [mobileOpen]);

  // Determine which nav links to show
  const getNavLinks = () => {
    if (!userProfile) return publicNavLinks;
    if (userProfile?.role === 'ADMIN') return adminNavLinks;
    if (userProfile?.role === 'PROFESSIONAL') return professionalNavLinks;
    if (userProfile?.role === 'OWNER') return ownerNavLinks;
    return publicNavLinks;
  };

  const navLinks = getNavLinks();
  const isAuthenticated = !!userProfile;
  const emailVerified = isEmailVerified();

  const cycleTheme = () => {
    if (theme === 'system') setTheme('light');
    else if (theme === 'light') setTheme('dark');
    else setTheme('system');
  };

  const ThemeIcon = () => {
    if (!mounted) return <span className="w-5 h-5 block" />;
    if (theme === 'dark') return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    );
    if (theme === 'light') return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    );
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H4a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2h-1" />
      </svg>
    );
  };

  const themeLabel = theme === 'dark' ? 'Dark' : theme === 'light' ? 'Light' : 'System';

  return (
    <header
      suppressHydrationWarning
      ref={menuRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 dark:bg-[#0F1A1A]/95 backdrop-blur-xl shadow-soft border-b border-[#E2EDED] dark:border-[#2A3F3F]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5 group">
          <AppLogo
            src="/assets/images/Logo_big-1771795433731.png"
            size={36}
            text="DogFob"
            className="group-hover:scale-105 transition-transform duration-200"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href || (link?.href !== '/' && pathname?.startsWith(link?.href));
            return (
              <Link
                key={link?.href}
                href={link?.href}
                className={`px-4 py-2 rounded-full text-sm font-medium font-heading transition-all duration-150 hover:scale-105 ${
                  isActive
                    ? "bg-teal-50 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 font-semibold" :"text-slate-700 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50/60 dark:hover:bg-teal-900/30"
                }`}
              >
                {link?.label}
              </Link>
            );
          })}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated && <NotificationBell />}

          {/* Theme Toggle */}
          <button
            onClick={cycleTheme}
            title={`Theme: ${themeLabel} — click to cycle`}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[#4A6363] dark:text-[#9DBDBD] hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-all duration-200 text-xs font-medium"
          >
            <ThemeIcon />
            <span className="hidden lg:inline">{themeLabel}</span>
          </button>

          {/* Email verification warning */}
          {isAuthenticated && user && !emailVerified && (
            <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 px-3 py-1.5 rounded-full border border-amber-200 dark:border-amber-700">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Verify email
            </span>
          )}

          {isAuthenticated ? (
            <>
              {/* Role badge */}
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${
                userProfile?.role === 'ADMIN' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' :
                userProfile?.role === 'PROFESSIONAL'? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300'
              }`}>
                {userProfile?.role === 'ADMIN' ? 'Admin' :
                 userProfile?.role === 'PROFESSIONAL' ? 'Professional' : 'Pet Owner'}
              </span>
              <button onClick={signOut} className="btn-secondary text-sm py-2.5 px-5">
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link href="/login" className="btn-secondary text-sm py-2.5 px-5">
                Sign In
              </Link>
              <Link href="/register" className="btn-primary text-sm py-2.5 px-5">
                Get Started
              </Link>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden flex items-center gap-2">
          {isAuthenticated && <NotificationBell />}
          {/* Mobile Theme Toggle */}
          <button
            onClick={cycleTheme}
            title={`Theme: ${themeLabel}`}
            className="p-2 rounded-lg text-[#4A6363] dark:text-[#9DBDBD] hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors"
          >
            <ThemeIcon />
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="flex flex-col gap-1.5 p-2 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors"
            aria-label="Toggle mobile menu"
          >
            <span className={`hamburger-line ${mobileOpen ? "rotate-45 translate-y-[7px]" : ""}`} />
            <span className={`hamburger-line ${mobileOpen ? "opacity-0" : ""}`} />
            <span className={`hamburger-line ${mobileOpen ? "-rotate-45 -translate-y-[7px]" : ""}`} />
          </button>
        </div>
      </div>
      {/* Mobile Menu */}
      <div
        className={`md:hidden bg-white dark:bg-[#0f172a] border-t border-[#E2EDED] dark:border-[#334155] overflow-hidden transition-all duration-300 ${
          mobileOpen ? "max-h-[500px] shadow-float" : "max-h-0"
        }`}
      >
        <nav className="px-5 py-4 flex flex-col gap-1">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href || (link?.href !== '/' && pathname?.startsWith(link?.href));
            return (
              <Link
                key={link?.href}
                href={link?.href}
                className={`px-4 py-3 rounded-xl text-sm font-medium font-heading transition-all ${
                  isActive
                    ? "bg-teal-50 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 font-semibold" :"text-slate-700 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50/60 dark:hover:bg-teal-900/30"
                }`}
              >
                {link?.label}
              </Link>
            );
          })}
          <div className="mt-3 flex flex-col gap-2 pt-3 border-t border-[#E2EDED] dark:border-[#2A3F3F]">
            {isAuthenticated ? (
              <>
                {isAuthenticated && user && !emailVerified && (
                  <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/30 rounded-xl text-xs text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700">
                    ⚠️ Please verify your email address
                  </div>
                )}
                <button onClick={signOut} className="btn-secondary text-sm py-3 justify-center">
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link href="/login" className="btn-secondary text-sm py-3 justify-center">
                  Sign In
                </Link>
                <Link href="/register" className="btn-primary text-sm py-3 justify-center">
                  Get Started
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}